/**
 * Unit Details Helpers
 *
 * Pure utility functions for the unit_details and unit_detail_changes collections.
 * Handles HVAC info, lock codes, filter sizes — everything that lives at the unit level.
 */

// ============================================
// FIELD ENUMS — dropdown options for UI forms
// ============================================

export const HEAT_TYPES = [
  'Furnace',
  'Boiler',
  'Electric Baseboard',
  'Mini-split',
  'Other',
];

export const AC_TYPES = [
  'Central Air',
  'Floor Unit',
  'Window Unit',
  'Wall Unit',
  'Mini-split',
];

export const LOCK_CODES = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
  'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T',
  'U', 'V', 'W', 'X', 'Y', 'Z', 'Other', 'None',
];

export const LOCK_TYPES = [
  'Deadbolt',
  'Locking Passage',
  'Keypad',
  'Other',
];

/** Human-readable labels for trackable fields (used in confirm modals & change history) */
export const FIELD_LABELS = {
  heatType: 'Heat Type',
  acType: 'AC Type',
  filterSize: 'Filter Size',
  hvacNotes: 'HVAC Notes',
  unitLockCode: 'Unit Lock Code',
  unitLockType: 'Unit Lock Type',
  unitLockCount: 'Unit Lock Count',
  unitLockNotes: 'Unit Lock Notes',
  tenantLockbox: 'Tenant Lockbox',
  commonLockCode: 'Common Lock Code',
  commonLockType: 'Common Lock Type',
  commonLockCount: 'Common Lock Count',
  commonLockNotes: 'Common Lock Notes',
};

/** Fields tracked in the changelog (unit_detail_changes) */
export const TRACKABLE_FIELDS = [
  'heatType',
  'acType',
  'filterSize',
  'hvacNotes',
  'unitLockCode',
  'unitLockType',
  'unitLockCount',
  'unitLockNotes',
  'tenantLockbox',
  'commonLockCode',
  'commonLockType',
  'commonLockCount',
  'commonLockNotes',
];

/**
 * Format all lock codes a unit has into one display string for list cards.
 * Single-family units have no common lock, so they naturally show one code.
 * @param {Object} details - unit_details doc (may be null/undefined)
 * @returns {string} e.g. "Q / R", "Q", or "" when there are no codes
 */
export function formatLockCodes(details) {
  return [details?.unitLockCode, details?.commonLockCode].filter(Boolean).join(' / ');
}

/**
 * Coerce lock-count fields to int|null before saving.
 * Number inputs hand back strings ("3"); without this, diffUnitDetails compares
 * "3" !== 3 against CSV-imported int counts and logs a phantom change every save.
 * Empty, null, non-numeric, and 0 all become null.
 *
 * @param {Object} values - form values (any subset of unit detail fields)
 * @returns {Object} copy with unitLockCount/commonLockCount as int|null
 */
export function coerceLockCounts(values) {
  const coerce = (v) => (v === '' || v == null) ? null : (parseInt(v, 10) || null);
  return {
    ...values,
    unitLockCount: coerce(values.unitLockCount),
    commonLockCount: coerce(values.commonLockCount),
  };
}

// ============================================
// DIFF & HISTORY
// ============================================

/**
 * Compare current unit details with new values, return list of changes.
 * Only tracks fields in TRACKABLE_FIELDS. Treats null/undefined/'' as equivalent (no data).
 *
 * @param {Object} current - Current unit details (from Firestore)
 * @param {Object} newValues - New values being saved
 * @returns {Array<{field: string, from: any, to: any}>}
 */
export function diffUnitDetails(current, newValues) {
  const changes = [];
  const normalize = (v) => (v === undefined || v === null || v === '') ? null : v;

  for (const field of TRACKABLE_FIELDS) {
    const from = normalize(current?.[field]);
    const to = normalize(newValues?.[field]);
    if (from !== to) {
      changes.push({ field, from, to });
    }
  }

  return changes;
}

/**
 * Extract history for a single field from changelog entries.
 * Returns entries sorted chronologically (oldest first).
 *
 * @param {Array} changeEntries - Array of unit_detail_changes docs
 * @param {string} fieldName - Field to extract history for
 * @returns {Array<{from: any, to: any, changedBy: string, createdAt: string}>}
 */
export function getFieldHistory(changeEntries, fieldName) {
  const history = [];

  for (const entry of changeEntries) {
    const match = (entry.changes || []).find(c => c.field === fieldName);
    if (match) {
      history.push({
        from: match.from,
        to: match.to,
        changedBy: entry.changedByName || entry.changedBy || 'Unknown',
        createdAt: entry.createdAt,
        notes: entry.notes || null,
      });
    }
  }

  // Sort chronologically (oldest first)
  history.sort((a, b) => {
    const aTime = typeof a.createdAt === 'string' ? a.createdAt : a.createdAt?.seconds || 0;
    const bTime = typeof b.createdAt === 'string' ? b.createdAt : b.createdAt?.seconds || 0;
    return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
  });

  return history;
}

// ============================================
// CSV PARSER — multiline-header master CSV
// ============================================

/**
 * Header keyword prefixes → field names.
 * The CSV has multiline headers with parenthetical notes — we match by the start of the header.
 */
const HEADER_MAP = [
  { prefix: 'Unit ID', field: 'unitId' },
  { prefix: 'Heat Type', field: 'heatType' },
  { prefix: 'AC Type', field: 'acType' },
  { prefix: 'Filter Size', field: 'filterSize' },
  { prefix: 'HVAC notes', field: 'hvacNotes' },
  { prefix: 'Code of Unit Lock', field: 'unitLockCode' },
  { prefix: 'UnitLockType', field: 'unitLockType' },
  { prefix: '# of Total Unit Lock', field: 'unitLockCount' },
  { prefix: 'Unit Lock Notes', field: 'unitLockNotes' },
  { prefix: 'Lockbox For tenant', field: 'tenantLockbox' },
  { prefix: 'Code of Common Lock', field: 'commonLockCode' },
  { prefix: 'CommonLockType', field: 'commonLockType' },
  { prefix: '# of Total Common Lock', field: 'commonLockCount' },
  { prefix: 'Common Lock Notes', field: 'commonLockNotes' },
];

/**
 * Parse the multiline-header unit details CSV.
 *
 * The CSV has headers that span multiple lines inside quotes, e.g.:
 *   "Heat Type\n(Furnace, Boiler, ...)"
 *
 * Strategy: parse RFC 4180 CSV properly (handling quoted fields with newlines),
 * then match headers by keyword prefix.
 *
 * @param {string} csvText - Raw CSV file content
 * @returns {Array<Object>} Parsed records with clean field names
 */
export function parseUnitDetailsCsv(csvText) {
  const rows = parseCSVRows(csvText);
  if (rows.length < 2) return [];

  // First row is headers — map each to a field name
  const headerRow = rows[0];
  const fieldMap = []; // index → fieldName

  for (let i = 0; i < headerRow.length; i++) {
    const raw = headerRow[i].replace(/\n/g, ' ').trim();
    const mapping = HEADER_MAP.find(m => raw.startsWith(m.prefix));
    fieldMap[i] = mapping ? mapping.field : null;
  }

  // Parse data rows
  const records = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.length === 0) continue;

    const record = {};
    let hasUnitId = false;

    for (let c = 0; c < row.length; c++) {
      const field = fieldMap[c];
      if (!field) continue;

      const val = row[c].trim();
      if (field === 'unitId') {
        if (!val) break; // Skip rows with no unitId
        record.unitId = val;
        hasUnitId = true;
      } else if (field === 'unitLockCount' || field === 'commonLockCount') {
        record[field] = val ? parseInt(val, 10) || null : null;
      } else {
        record[field] = val || null;
      }
    }

    if (hasUnitId) {
      records.push(record);
    }
  }

  return records;
}

/**
 * RFC 4180 CSV parser that handles quoted fields with embedded newlines and commas.
 * Returns array of rows, each row an array of string values.
 */
function parseCSVRows(text) {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          // Escaped quote
          currentField += '"';
          i += 2;
        } else {
          // End of quoted field
          inQuotes = false;
          i++;
        }
      } else {
        currentField += ch;
        i++;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
        i++;
      } else if (ch === ',') {
        currentRow.push(currentField);
        currentField = '';
        i++;
      } else if (ch === '\n' || ch === '\r') {
        currentRow.push(currentField);
        currentField = '';
        if (ch === '\r' && i + 1 < text.length && text[i + 1] === '\n') {
          i++; // Skip \r\n
        }
        i++;
        if (currentRow.length > 0) {
          rows.push(currentRow);
        }
        currentRow = [];
      } else {
        currentField += ch;
        i++;
      }
    }
  }

  // Final field/row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  return rows;
}
